import { useCallback, useLayoutEffect, useMemo } from 'react';

import { actions } from 'components/conversation/actions/conversation';
import { useBreakpointValue } from 'utils/mediaQuery';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { useServerStorage } from 'utils/useServerStorage';

type CardId = 'elevio' | 'knowledge' | (string & {});

type UseConversationSidebarSettingsResult = {
  readonly cardOpenState: Readonly<Record<CardId, boolean>>;
  readonly isSidebarOpen: boolean;
  readonly setCardOpen: (key: CardId, open: boolean) => void;
  readonly setSidebarOpen: (open: boolean) => void;
  readonly toggleSidebar: () => void;
};

/**
 * A hook to manage the state and behavior of the conversation sidebar.
 *
 * The open/close state of the sidebar is dictated by the screen size. On
 * smaller screens, the sidebar is closed by default and can be toggled open,
 * without persisting this state on the server. On larger screens, the sidebar's
 * open/close state is persisted in server storage.
 */
export const useConversationSidebarSettings =
  (): UseConversationSidebarSettingsResult => {
    const { data, set } = useServerStorage('user');
    const dispatch = useAppDispatch();
    const isSmallLayoutSidebarOpen = useAppSelector(
      (state) => state.conversation.isSmallLayoutSidebarOpen,
    );

    const isSmallLayout = useBreakpointValue({
      base: true,
      lg: false,
    });

    const cardOpenState = useMemo(
      () => data.conversation?.sidebarCardOpenState ?? {},
      [data.conversation?.sidebarCardOpenState],
    );

    const setCardOpen: UseConversationSidebarSettingsResult['setCardOpen'] =
      useCallback(
        (key, open) => {
          set({
            conversation: (draft) => {
              draft.sidebarCardOpenState = {
                ...draft.sidebarCardOpenState,
                [key]: open,
              };
            },
          });
        },
        [set],
      );

    const isSidebarOpen =
      (isSmallLayout
        ? isSmallLayoutSidebarOpen
        : data.conversation?.isSidebarOpen) ?? false;

    const setSidebarOpen: UseConversationSidebarSettingsResult['setSidebarOpen'] =
      useCallback(
        (open) => {
          if (isSmallLayout) {
            dispatch(actions.conversationSetSmallLayoutSidebarOpen(open));
          } else {
            set({
              conversation: (draft) => {
                draft.isSidebarOpen = open;
              },
            });
          }
        },
        [set, isSmallLayout, dispatch],
      );

    const toggleSidebar: UseConversationSidebarSettingsResult['toggleSidebar'] =
      useCallback(() => {
        if (isSmallLayout) {
          dispatch(
            actions.conversationSetSmallLayoutSidebarOpen(
              !isSmallLayoutSidebarOpen,
            ),
          );
        } else {
          set({
            conversation: (draft) => {
              draft.isSidebarOpen = !draft.isSidebarOpen;
            },
          });
        }
      }, [set, isSmallLayout, dispatch, isSmallLayoutSidebarOpen]);

    useLayoutEffect(() => {
      // Reset sidebar when layout changed to small.
      if (isSmallLayout && isSmallLayoutSidebarOpen) {
        dispatch(actions.conversationSetSmallLayoutSidebarOpen(false));
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSmallLayout]);

    return useMemo(
      () => ({
        cardOpenState,
        isSidebarOpen,
        setCardOpen,
        setSidebarOpen,
        toggleSidebar,
      }),
      [
        cardOpenState,
        isSidebarOpen,
        setCardOpen,
        setSidebarOpen,
        toggleSidebar,
      ],
    );
  };
